import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { PuffLoader } from "react-spinners";
import { ExclamationTriangle } from "react-bootstrap-icons";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/20/solid";
import { Dropdown, DropdownButton, DropdownBody, DropdownItem } from "@components/atoms/Dropdown";
import { useUser } from "@auth";
import { PageHeader, PageTitle } from "@components";
import { toast } from "@components/toast/Toast";
import { fetchPaymentDetails, sendScheduledRequest } from "@api";
import { cancelScheduledAction } from "@api/actionsController";
import { formatAmount } from "@utils";
import TaskCard from "../components/TaskCard";
import TaskFilters from "../components/TaskFilters";
import HomeTab from "../components/HomeTab";
import ActivityTab from "../components/ActivityTab";
import { useTasks } from "../hooks/useTasks";
import { useWorkingSets } from "../hooks/workingSets";
import { useActivityFeed } from "../hooks/useActivityFeed";
import { TASK_FILTERS, DEFAULT_FILTER, isOverdue } from "../hooks/taskFilters";
import EditReminderModal from "../../payments/flows/EditReminderModal";
import PlanDetailModal from "../../calendar/displays/PlanDetailModal";
import { preferredReminderChannel, sendDefaultReminder } from "../../payments/flows/reminder";

// Requests already paid/closed elsewhere can't be reminded (payment-service also rejects them).
const NON_SENDABLE_STATUSES = new Set([
  "Paid",
  "Part-paid",
  "Cancelled",
  "Expired",
  "Refunded",
  "Part-refund",
]);
const isSendable = (paymentRequest) =>
  !NON_SENDABLE_STATUSES.has(paymentRequest?.displayStatus);

const TAB_STORAGE_KEY = "tasks.tab";
const TABS = ["home", "tasks", "activity"];
const TAB_LABELS = { home: "Home", tasks: "Tasks", activity: "Activity" };

const readStoredTab = () => {
  try {
    const stored = localStorage.getItem(TAB_STORAGE_KEY);
    return TABS.includes(stored) ? stored : "home";
  } catch {
    return "home";
  }
};

const PageTabs = ({ active, counts, onChange }) => (
  <div className="flex gap-8 border-b border-zinc-200" role="tablist" aria-label="Today's tasks sections">
    {TABS.map((key) => {
      const isActive = key === active;
      return (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(key)}
          className={
            "-mb-px border-b-2 px-0.5 pb-3 text-[15px] transition-colors " +
            (isActive
              ? "border-zinc-900 font-semibold text-zinc-900"
              : "border-transparent font-medium text-zinc-400 hover:text-zinc-600")
          }
        >
          {TAB_LABELS[key]}
          <span className={"ml-1.5 text-[13px] " + (isActive ? "font-medium text-zinc-500" : "")}>
            ({counts[key] ?? 0})
          </span>
        </button>
      );
    })}
  </div>
);

const SORT_STORAGE_KEY = "tasks.sort";
const DEFAULT_SORT = "recommended";
const SORTS = [
  { key: "recommended", label: "Recommended" },
  { key: "newest", label: "Sort by newest" },
  { key: "oldest", label: "Sort by oldest" },
  { key: "amount", label: "Sort by £" },
];

const readStoredSort = () => {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    return SORTS.some((s) => s.key === stored) ? stored : DEFAULT_SORT;
  } catch {
    return DEFAULT_SORT;
  }
};

// Client-side re-sort of the visible list. "recommended" keeps the BFF's priority order untouched;
// the others re-sort and sink tasks without the sorted-on value to the bottom.
const sortTasks = (list, sort) => {
  if (sort === "recommended") return list;
  const copy = [...list];
  if (sort === "amount") {
    copy.sort((a, b) => (b.amount ?? -Infinity) - (a.amount ?? -Infinity));
    return copy;
  }
  const dir = sort === "oldest" ? 1 : -1;
  copy.sort((a, b) => {
    const av = a.due ? a.due.getTime() : null;
    const bv = b.due ? b.due.getTime() : null;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * dir;
  });
  return copy;
};

const greetingFor = (date) => {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

// One section of the page (outstanding / snoozed / completed today), keeping the card exit
// animation each row had when the page was a single list.
const TaskSection = ({ title, tasks, exiting, pending, variant, onDismiss, onPreview, onSend, onComplete, onOpen, canOpen, emptyText }) => (
  <section className="flex flex-col">
    {title && (
      <h3 className="pb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">{title}</h3>
    )}
    {tasks.length === 0 && emptyText ? (
      <p className="pb-3 text-sm text-zinc-500">{emptyText}</p>
    ) : (
      tasks.map((task) => (
        <div
          key={task.id}
          className={
            "grid transition-all duration-300 ease-in-out motion-reduce:transition-none " +
            (exiting.has(task.id)
              ? "grid-rows-[0fr] opacity-0 -translate-x-2"
              : "grid-rows-[1fr] opacity-100")
          }
        >
          <div className="min-h-0 overflow-hidden">
            <div className="pb-6">
              <TaskCard
                task={task}
                showAccountLink
                variant={variant}
                onDismiss={onDismiss}
                onPreview={onPreview}
                onSend={onSend}
                onComplete={onComplete}
                onOpen={!canOpen || canOpen(task) ? onOpen : undefined}
                sending={pending?.id === task.id && pending.action === "send"}
                previewing={pending?.id === task.id && pending.action === "preview"}
              />
            </div>
          </div>
        </div>
      ))
    )}
  </section>
);

const TasksV2 = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const firstName = user?.name?.split(" ")[0];
  const today = new Date();

  const {
    tasks, outstanding, outstandingAmount,
    exiting, isLoading, error, dismiss, complete, markDone, decide,
  } = useTasks();

  // A decision-graph review carries the parked step it resolves; a linear-path review does not. That
  // one field routes the card's Send anyway / Dismiss to the decision endpoint vs the old flow.
  const isDecisionReview = (task) =>
    task.taskType === "IncomingPaymentReview" && !!task.stepId;

  const { sets: workingSets, isLoading: workingSetsLoading } = useWorkingSets();
  const activityFeed = useActivityFeed();

  const [tab, setTab] = useState(readStoredTab);
  const changeTab = (key) => {
    setTab(key);
    try {
      localStorage.setItem(TAB_STORAGE_KEY, key);
    } catch {
      // Ignore storage failures (private mode / quota) — the tab still applies for this session.
    }
  };

  // Each filter pill is a predicate over the same task list, so counts and the shown list stay in
  // step. Exiting tasks are kept in the count until their row finishes animating out.
  const [activeFilter, setActiveFilter] = useState(DEFAULT_FILTER);
  const [sort, setSort] = useState(readStoredSort);
  const activeSort = SORTS.find((s) => s.key === sort) ?? SORTS[0];
  const changeSort = (key) => {
    setSort(key);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, key);
    } catch {
      // Ignore storage failures (private mode / quota) — the sort still applies for this session.
    }
  };

  const filterCounts = TASK_FILTERS.reduce((acc, f) => {
    acc[f.key] = f.select(tasks).length;
    return acc;
  }, {});

  const activeFilterDef = TASK_FILTERS.find((f) => f.key === activeFilter) ?? TASK_FILTERS[0];
  const visibleTasks = sortTasks(activeFilterDef.select(tasks), sort);
  const isClearedTab = activeFilter === "cleared";
  const emptyText = isClearedTab
    ? "Nothing cleared yet today."
    : activeFilter === "critical"
      ? "No critical tasks right now."
      : "Nothing here right now.";
  const tabCounts = {
    home: workingSets.length,
    tasks: filterCounts.all ?? 0,
    activity: activityFeed.items.length,
  };

  // Header line, from the same response the cards come from — counts can't disagree with the list.
  // Overdue tasks have their own filter tab, so the subtitle counts only today's outstanding work.
  const todayOutstanding = outstanding.filter((t) => !isOverdue(t));
  const todayCriticalCount = todayOutstanding.filter((t) => t.priority?.band === "critical").length;
  const summary =
    todayCriticalCount > 0
      ? `There ${todayCriticalCount === 1 ? "is 1 critical task" : `are ${todayCriticalCount} critical tasks`} across ${formatAmount(outstandingAmount)} exposure. The rest can be picked up after.`
      : todayOutstanding.length > 0
        ? `You have ${todayOutstanding.length} ${todayOutstanding.length === 1 ? "task" : "tasks"} to work through today.`
        : null;

  // The edit-reminder modal target: the task plus its fetched payment-request detail. Opened by
  // Preview, or by Send when there's no contact info to send to directly.
  const [editTarget, setEditTarget] = useState(null);
  // Which card has a send/preview in flight — { id, action } — so its buttons show a loader/disable.
  const [pending, setPending] = useState(null);
  // The plan/promise whose detail modal is open — { actionId, focusInstalment } — opened by clicking
  // a task card that has a backing action.
  const [planTarget, setPlanTarget] = useState(null);
  const [cancellingPlan, setCancellingPlan] = useState(false);

  // The whole card is "view context": open the Smart Note flow scoped to this customer. Falls back
  // to the plan/promise detail modal only when the Smart Note feature is off.
  const { openSmartNote } = useOutletContext() ?? {};
  const canOpen = (task) => (openSmartNote ? !!task.customerId : !!task.actionId);

  const onOpen = (task) => {
    if (openSmartNote) {
      openSmartNote({ id: task.customerId, name: task.customerName });
      return;
    }
    setPlanTarget({ actionId: task.actionId, focusInstalment: task.instalmentNumber });
  };

  const onCancelPlan = async (actionId) => {
    setCancellingPlan(true);
    try {
      await cancelScheduledAction(actionId);
      await queryClient.invalidateQueries({ queryKey: ["actionInstalments", actionId] });
      await queryClient.invalidateQueries({ queryKey: ["todaysTasks"] });
      setPlanTarget(null);
      toast({ description: "Scheduled work cancelled." });
    } catch {
      toast({ description: "Couldn't cancel this. Please try again." });
    } finally {
      setCancellingPlan(false);
    }
  };

  // Dismiss: a decision-graph review answers the parked step ("dismiss" continues the plan); every
  // other task is the plain calendar-item cancel.
  const onDismiss = (task) => {
    if (isDecisionReview(task)) {
      decide(task, "dismiss");
      return;
    }
    dismiss(task);
  };

  const onSend = async (task) => {
    if (task.taskType === "IncomingPaymentReview") {
      // Decision-graph review: "send anyway" resumes the in-action send chain; the step exits the
      // queue and the card animates out — no separate complete (there's no calendar item to close).
      if (isDecisionReview(task)) {
        decide(task, "send_anyway");
        toast({ description: `Sending the payment request to ${task.customerName || "the customer"}.` });
        return;
      }
      if (!task.actionId) return;
      setPending({ id: task.id, action: "send" });
      try {
        await sendScheduledRequest(task.id);
        toast({ description: `Payment request sent to ${task.customerName || "the customer"}.` });
        await complete(task);
      } catch (error) {
        toast({
          description:
            error?.response?.status === 400
              ? "Couldn't send — no contact details on file."
              : "Couldn't send the payment request. Please try again.",
        });
      } finally {
        setPending(null);
      }
      return;
    }

    if (!task.paymentRequestId) return;
    setPending({ id: task.id, action: "send" });
    try {
      let detail;
      try {
        detail = await fetchPaymentDetails(task.paymentRequestId);
      } catch {
        toast({ description: "Couldn't load the payment request. Please try again." });
        return;
      }

      if (!isSendable(detail)) {
        toast({ description: "This request has already been paid or closed." });
        return;
      }

      // No email or phone on file — open the editor so the user can supply a recipient.
      const channel = preferredReminderChannel(detail);
      if (!channel) {
        setEditTarget({ task, detail });
        return;
      }

      // One click: send the default reminder (email preferred, else SMS) and close the task.
      try {
        await sendDefaultReminder(detail, channel);
      } catch {
        toast({ description: "Couldn't send the reminder. Please try again." });
        return;
      }
      toast({ description: `Reminder sent to ${task.customerName || "the customer"}.` });
      await complete(task);
    } finally {
      setPending(null);
    }
  };

  const onPreview = async (task) => {
    if (!task.paymentRequestId) return;
    setPending({ id: task.id, action: "preview" });
    try {
      let detail;
      try {
        detail = await fetchPaymentDetails(task.paymentRequestId);
      } catch {
        toast({ description: "Couldn't load the reminder. Please try again." });
        return;
      }
      setEditTarget({ task, detail });
    } finally {
      setPending(null);
    }
  };

  const onEditSent = async () => {
    if (editTarget?.task) {
      await complete(editTarget.task);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader>
        <PageTitle>Today's tasks</PageTitle>
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto bg-white">
        {/* Cap the column at the width it has with the right rail collapsed (viewport − left sidebar −
            collapsed rail) and centre it; opening the rail just narrows it. */}
        <div className="mx-auto w-full lg:max-w-[calc(100vw_-_17.2rem_-_48px)]">
          <div className="flex flex-col gap-4 px-6 pt-10">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium leading-5 text-zinc-600">
                {format(today, "EEEE, d MMMM yyyy")}
              </p>
              <h2 className="text-[32px] font-semibold tracking-[-0.28px] text-zinc-800">
                {greetingFor(today)}
                {firstName ? `, ${firstName}` : ""}
              </h2>
            </div>
            {summary && <p className="text-sm font-medium leading-5 text-zinc-600">{summary}</p>}
          </div>

          <div className="px-6 pt-8">
            <PageTabs active={tab} counts={tabCounts} onChange={changeTab} />
          </div>

          <div className="px-6 pb-8 pt-8">
            {tab === "home" ? (
              <HomeTab workingSets={workingSets} workingSetsLoading={workingSetsLoading} />
            ) : tab === "activity" ? (
              <ActivityTab feed={activityFeed} />
            ) : error ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-zinc-200 bg-white p-10 text-zinc-500">
                <ExclamationTriangle className="size-8 text-yellow-500" />
                <p className="text-sm font-medium text-zinc-700">Couldn't load your tasks</p>
                <p className="text-sm">Please try again shortly.</p>
              </div>
            ) : isLoading ? (
              <div className="flex justify-center py-16">
                <PuffLoader color="#F95816" size={64} />
              </div>
            ) : tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-zinc-200 p-10 text-center">
                <p className="text-sm font-medium text-zinc-700">You're all caught up</p>
                <p className="text-sm text-zinc-500">No tasks need your attention today.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between gap-4">
                  <TaskFilters
                    counts={filterCounts}
                    active={activeFilter}
                    onChange={setActiveFilter}
                    allDot
                  />
                  <Dropdown>
                    <DropdownButton className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-zinc-500 hover:text-zinc-800">
                      <span>{activeSort.label}</span>
                      <ChevronDownIcon aria-hidden="true" className="size-3 text-zinc-400" />
                    </DropdownButton>
                    <DropdownBody position="left" className="w-44">
                      {SORTS.map((s) => (
                        <DropdownItem key={s.key} onClick={() => changeSort(s.key)} closeOnComplete>
                          <span className="flex flex-1 items-center justify-between gap-2 text-sm">
                            {s.label}
                            {s.key === sort && <CheckIcon className="size-4 text-primary-500" />}
                          </span>
                        </DropdownItem>
                      ))}
                    </DropdownBody>
                  </Dropdown>
                </div>
                {visibleTasks.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">
                    {emptyText}
                  </div>
                ) : (
                  <TaskSection
                    tasks={visibleTasks}
                    exiting={exiting}
                    pending={pending}
                    variant={isClearedTab ? "completed" : "outstanding"}
                    onDismiss={onDismiss}
                    onPreview={onPreview}
                    onSend={onSend}
                    onComplete={markDone}
                    onOpen={onOpen}
                    canOpen={canOpen}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {editTarget && (
        <EditReminderModal
          isOpen={!!editTarget}
          paymentRequest={editTarget.detail}
          onClose={() => setEditTarget(null)}
          onSent={onEditSent}
        />
      )}

      {planTarget && (
        <PlanDetailModal
          actionId={planTarget.actionId}
          isOpen={!!planTarget}
          onClose={() => setPlanTarget(null)}
          onCancelAction={onCancelPlan}
          cancelling={cancellingPlan}
          focusInstalment={planTarget.focusInstalment}
        />
      )}
    </div>
  );
};

export default TasksV2;
